"use client";

import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { NavigateNext } from "@mui/icons-material";
import {
  Alert,
  AlertProps,
  Breadcrumbs,
  Link,
  Snackbar,
  SnackbarProps,
} from "@mui/material";

import { UiContextType } from "@/types/ui";
import { PageMetadata } from "@/types/util";

const UiContext = createContext<UiContextType | undefined>(undefined);

const defaultMetadata = {
  needsAuth: false,
  title: "Account Utilities",
  url: "",
};

export function UiProvider({ children }: { children: React.ReactNode }) {
  const [avatar, setAvatar] = useState<string | null>(null);
  const [alert, setAlert] = useState<ReactNode | null>(null);
  const [alerts, setAlerts] = useState<Map<number, ReactNode | null> | null>(
    null
  );
  const [backgroundColor, setBackgroundColor] =
    useState<string>("primary.main");
  const [breadcrumbs, setBreadcrumbs] = useState<ReactElement>(<></>);
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState<PageMetadata>(defaultMetadata);
  const [snackbar, setSnackbar] = useState<SnackbarProps & AlertProps>({
    message: "",
    open: false,
    severity: "info",
  });

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    setIsMobileDevice(/Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua));
  }, []);

  useEffect(() => {
    setBreadcrumbs(
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          backgroundColor: backgroundColor,
          color:
            backgroundColor === "primary.main"
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(0, 0, 0, 0.6)",
          fontSize: "0.75rem",
          margin: 0,
          padding: "0.5rem 0",
        }}
      >
        <Link
          underline="hover"
          key="1"
          color="inherit"
          href="/account-utilities"
        >
          Account Utilities
        </Link>
        {metadata.url !== "" && (
          <Link
            underline="hover"
            key="2"
            color="inherit"
            href={`/account-utilities${metadata.url}`}
          >
            {metadata.title}
          </Link>
        )}
      </Breadcrumbs>
    );
  }, [backgroundColor, metadata.url, metadata.title]);

  function handleSetAlert(keys: number[], value: ReactNode | null) {
    setAlerts((prev) => {
      const newAlerts = new Map(prev);
      keys.map((key) => newAlerts.set(key, value));
      return newAlerts;
    });
  }

  function handleSnackbarClose(
    _?: React.SyntheticEvent | Event,
    reason?: string
  ) {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  }

  const needsBackgroundColorUpdate = useCallback(
    (newBackgroundColor: string) => backgroundColor !== newBackgroundColor,
    [backgroundColor]
  );

  return (
    <UiContext.Provider
      value={{
        alert,
        alerts,
        avatar,
        backgroundColor,
        breadcrumbs,
        handleSetAlert,
        loading,
        metadata,
        isMobileDevice,
        needsBackgroundColorUpdate,
        setAlert,
        setAlerts,
        setAvatar,
        setBackgroundColor,
        setLoading,
        setMetadata,
        setSnackbar,
      }}
    >
      {children}
      <Snackbar
        {...snackbar}
        open={snackbar.open}
        onClose={handleSnackbarClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </UiContext.Provider>
  );
}

export const useUi = () => {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error("useUi must be used within an UiContext");
  return ctx;
};
